import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import handPrintLogo from "@/res/hand-print-logo.png";
import UserAuthMenu from "@/components/UserAuthMenu";
import InstallPWA from "@/components/InstallPWA";
import MobileBottomNav from "@/components/MobileBottomNav";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const menuItems = [
  { label: "Home", path: "/" },
  { label: "Stories", path: "/stories" },
  { label: "Printables", path: "/printables" },
  { label: "Videos", path: "/yt-gallery" },
  { label: "Games", path: "/games" },
  { label: "Pricing", path: "/pricing" },
];

const Navigation = () => {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);

  return (
    <>
    <nav className="bg-cyan-200 py-2 md:py-3 sticky top-0 z-50">
      {/* Cloud decorations in header */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-2 left-[8%] w-24 h-12 bg-cyan-100/50 rounded-full blur-sm" />
        <div className="absolute -top-1 right-[12%] w-20 h-10 bg-cyan-100/40 rounded-full blur-sm" />
        <div className="absolute top-1 left-[40%] w-16 h-8 bg-cyan-100/35 rounded-full blur-sm" />
      </div>

      <div className="container mx-auto px-4 flex justify-between items-center relative z-10">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 group flex-shrink-0">
          <motion.img
            src={handPrintLogo}
            alt="TinyMinds Logo - Educational learning platform for kids"
            className="w-8 h-8 object-contain"
            width="32"
            height="32"
            loading="eager"
            whileHover={{ rotate: 15, scale: 1.1 }}
            transition={{ type: "spring", stiffness: 300 }}
          />
          <span className="text-2xl md:text-3xl font-fredoka font-bold bg-gradient-to-r from-cyan-600 via-teal-500 to-emerald-500 bg-clip-text text-transparent group-hover:from-orange-400 group-hover:via-pink-500 group-hover:to-purple-500 transition-all duration-500">
            TinyMinds
          </span>
        </Link>

        {/* Center pill menu - desktop */}
        <div className="hidden md:flex items-center bg-white/70 backdrop-blur-sm rounded-full px-2 py-1.5 border border-cyan-300/50 shadow-sm">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`font-fredoka text-base px-4 py-1.5 rounded-full transition-all duration-300 ${
                  isActive
                    ? "bg-cyan-400 text-white shadow-md"
                    : "text-gray-700 hover:text-cyan-600 hover:bg-cyan-100/60"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>

        {/* Right side: install + auth (desktop) / hamburger (mobile) */}
        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:flex items-center gap-3">
            <InstallPWA variant="compact" />
            <UserAuthMenu />
          </div>

          {/* Mobile: compact auth + hamburger */}
          <div className="md:hidden flex items-center gap-1.5">
            <InstallPWA variant="icon" />
            <UserAuthMenu />
            <Sheet open={openMenu} onOpenChange={setOpenMenu}>
              <SheetTrigger asChild>
                <button
                  aria-label="Open menu"
                  className="w-10 h-10 rounded-full bg-white/90 border border-cyan-300/60 shadow-sm flex items-center justify-center text-cyan-700 active:scale-95 transition"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[80vw] max-w-xs p-0 bg-gradient-to-b from-cyan-50 to-white">
                <div className="flex items-center justify-between px-5 py-4 border-b border-cyan-100">
                  <span className="font-fredoka font-bold text-lg text-cyan-700">Menu</span>
                  <button
                    aria-label="Close menu"
                    onClick={() => setOpenMenu(false)}
                    className="w-9 h-9 rounded-full bg-white border border-cyan-200 flex items-center justify-center text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <ul className="px-3 py-3 flex flex-col gap-1">
                  {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setOpenMenu(false)}
                          className={`block px-4 py-3 rounded-2xl font-fredoka font-semibold text-base transition ${
                            isActive
                              ? "bg-cyan-400 text-white shadow"
                              : "text-gray-700 hover:bg-cyan-100"
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                  <li className="mt-2 border-t border-cyan-100 pt-3">
                    <Link
                      to="/parent-dashboard"
                      onClick={() => setOpenMenu(false)}
                      className="block px-4 py-3 rounded-2xl font-fredoka font-semibold text-base text-gray-700 hover:bg-cyan-100"
                    >
                      Parent Dashboard
                    </Link>
                    <Link
                      to="/pricing"
                      onClick={() => setOpenMenu(false)}
                      className="block px-4 py-3 rounded-2xl font-fredoka font-semibold text-base text-gray-700 hover:bg-cyan-100"
                    >
                      Pricing
                    </Link>
                    <Link
                      to="/about"
                      onClick={() => setOpenMenu(false)}
                      className="block px-4 py-3 rounded-2xl font-fredoka font-semibold text-base text-gray-700 hover:bg-cyan-100"
                    >
                      About
                    </Link>
                  </li>
                  <li className="px-2 pt-3">
                    <InstallPWA variant="compact" />
                  </li>
                </ul>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
    <MobileBottomNav />
    </>
  );
};

export default Navigation;
